import type { ReactNode } from 'react';

import DeleteOutline from '@mui/icons-material/DeleteOutlined';
import { ButtonBase, Typography } from '@mui/material';

import type { Sx } from '@/shared/types/uiTypes';

import { useDisclosure } from '@/shared/hooks/useDisclosure';
import { normalizeSx } from '@/shared/lib/normalizeSx';
import { Dialog, DialogButton } from '@/shared/ui/overlays/Modal';

const buttonSx = {
  display: 'flex',
  width: '32px',
  height: '32px',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'gx.textDisabled',
  border: 'none',
  outline: 'none',
  cursor: 'pointer',
  opacity: 1,
  ml: 'auto',
  transition: 'background-color 0.2s ease, opacity 0.2s ease',
  '&:hover': { backgroundColor: 'gx.infoMain', opacity: 0.9 }
};
const iconSx = { fontSize: 16, color: 'gx.text' };

export const ConfirmRemoveButton = ({
  title,
  description,
  onConfirm,
  icon = <DeleteOutline sx={iconSx} />,
  triggerSx,
  className,
  ariaLabel = 'Remove item'
}: {
  title: string;
  description: string;
  onConfirm: () => void;
  /** Trigger icon, defaults to the standard delete icon. */
  icon?: ReactNode;
  /** Additional styles merged onto the trigger button, applied after the built-in styles. */
  triggerSx?: Sx;
  className?: string;
  ariaLabel?: string;
}) => {
  const { isOpen, open, close } = useDisclosure();

  return (
    <>
      <ButtonBase
        aria-label={ariaLabel}
        className={className}
        sx={[buttonSx, ...normalizeSx(triggerSx)]}
        onClick={open}
      >
        {icon}
      </ButtonBase>
      <Dialog
        buttons={
          <>
            <DialogButton
              variant='outlined'
              onClick={close}
            >
              Cancel
            </DialogButton>
            <DialogButton
              variant='contained'
              onClick={() => {
                onConfirm();
                close();
              }}
            >
              Remove
            </DialogButton>
          </>
        }
        open={isOpen}
        title={title}
        onClose={close}
      >
        <Typography>{description}</Typography>
      </Dialog>
    </>
  );
};
